import type { StaticHandlerContext } from 'react-router';

const SITE_URL = 'https://yrityksille.elisa.fi/ideat';
const SITE_NAME = 'Elisa Ideat Yrityksille';
const SITE_DESCRIPTION = 'Ajankohtaisia aiheita digitaalisista palveluista, pilvestä ja asioiden Internetistä';
const ORG_NAME = 'Elisa Yrityksille';
const ORG_URL = 'https://yrityksille.elisa.fi';
const ORG_LOGO_URL = 'https://yrityksille.elisa.fi/images/logo-elisa.png';

const websiteId = `${SITE_URL}#website`;
const organizationId = `${ORG_URL}#organization`;
const logoId = `${ORG_URL}#logo`;

export type CollectionJsonLdProps = {
  type: 'collection';
  title: string;
  description: string;
};

export type ArticleJsonLdProps = {
  type: 'article';
  title: string;
  description: string;
  featuredImageUrl: string;
  publishedOn: string;
  updatedOn?: string;
  categoryNames: string[];
  tagNames: string[];
  author?: {
    id: string;
    name: string;
    description?: string;
    profileImageUrl?: string;
  };
};

export type IdeatJsonLdProps = CollectionJsonLdProps | ArticleJsonLdProps;

/* eslint-disable @typescript-eslint/naming-convention */
const buildCollectionJsonLd = (canonicalUrl: string, props: CollectionJsonLdProps) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      '@id': `${canonicalUrl}#collection`,
      url: canonicalUrl,
      name: props.title,
      isPartOf: { '@id': websiteId },
      about: { '@id': organizationId },
      description: props.description,
      inLanguage: 'fi',
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: SITE_URL,
      name: SITE_NAME,
      description: SITE_DESCRIPTION,
      publisher: { '@id': organizationId },
      inLanguage: 'fi',
    },
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: ORG_NAME,
      url: ORG_URL,
      logo: {
        '@type': 'ImageObject',
        '@id': logoId,
        inLanguage: 'fi',
        url: ORG_LOGO_URL,
        caption: 'Elisa',
      },
      image: { '@id': logoId },
    },
  ],
});

const buildArticleJsonLd = (canonicalUrl: string, props: ArticleJsonLdProps) => {
  const pageId = canonicalUrl;
  const articleId = `${canonicalUrl}#article`;
  const primaryImageId = `${canonicalUrl}#primaryimage`;

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Article',
      '@id': articleId,
      headline: props.title,
      datePublished: props.publishedOn,
      ...(props.updatedOn && { dateModified: props.updatedOn }),
      mainEntityOfPage: { '@id': pageId },
      isPartOf: { '@id': pageId },
      image: { '@id': primaryImageId },
      thumbnailUrl: props.featuredImageUrl,
      keywords: props.tagNames,
      articleSection: props.categoryNames,
      inLanguage: 'fi',
      ...(props.author && {
        author: { '@type': 'Person', '@id': `${ORG_URL}/#/author/${props.author.id}` },
      }),
      publisher: { '@id': organizationId },
    },
    {
      '@type': 'WebPage',
      '@id': pageId,
      url: canonicalUrl,
      name: props.title,
      primaryImageOfPage: { '@id': primaryImageId },
      image: { '@id': primaryImageId },
      thumbnailUrl: props.featuredImageUrl,
      datePublished: props.publishedOn,
      ...(props.updatedOn && { dateModified: props.updatedOn }),
      description: props.description,
      inLanguage: 'fi',
      isPartOf: { '@id': websiteId },
      potentialAction: [{ '@type': 'ReadAction', target: [canonicalUrl] }],
    },
    {
      '@type': 'ImageObject',
      '@id': primaryImageId,
      inLanguage: 'fi',
      url: props.featuredImageUrl,
      contentUrl: props.featuredImageUrl,
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: SITE_URL,
      name: SITE_NAME,
      description: SITE_DESCRIPTION,
      publisher: { '@id': organizationId },
      inLanguage: 'fi',
    },
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: ORG_NAME,
      url: ORG_URL,
      logo: {
        '@type': 'ImageObject',
        '@id': logoId,
        inLanguage: 'fi',
        url: ORG_LOGO_URL,
        caption: 'Elisa',
      },
      image: { '@id': logoId },
    },
  ];

  if (props.author) {
    const authorNode: Record<string, unknown> = {
      '@type': 'Person',
      '@id': `${ORG_URL}/#/author/${props.author.id}`,
      name: props.author.name,
    };
    if (props.author.profileImageUrl) {
      authorNode.image = {
        '@type': 'ImageObject',
        inLanguage: 'fi',
        url: props.author.profileImageUrl,
        contentUrl: props.author.profileImageUrl,
        caption: props.author.name,
      };
    }
    if (props.author.description) {
      authorNode.description = props.author.description;
    }
    graph.push(authorNode);
  }

  return { '@context': 'https://schema.org', '@graph': graph };
};
/* eslint-enable @typescript-eslint/naming-convention */

export const buildIdeatJsonLd = (canonicalUrl: string, props: IdeatJsonLdProps): string =>
  JSON.stringify(
    props.type === 'collection' ? buildCollectionJsonLd(canonicalUrl, props) : buildArticleJsonLd(canonicalUrl, props)
  );

export const escapeJsonLd = (json: string): string => json.replace(/</g, String.raw`\u003c`);

export const getIdeatJsonLdProps = (ctx: StaticHandlerContext): IdeatJsonLdProps | undefined => {
  const article = ctx.loaderData['ideat-article-root']?.article;
  if (article) {
    return {
      type: 'article',
      title: article.meta.title,
      description: article.meta.description,
      featuredImageUrl: article.featuredImageUrl,
      publishedOn: article.publishedOn,
      categoryNames: article.categories.map((c: { title: string }) => c.title),
      tagNames: article.tags?.map((tag: { title: string }) => tag.title) ?? [],
      author: article.author
        ? {
            id: article.author.name,
            name: article.author.name,
            description: article.author.description,
            profileImageUrl: article.author.profileImageUrl,
          }
        : undefined,
    };
  }

  for (const id of ['ideat-category-root', 'ideat-category-page', 'ideat-tag-root', 'ideat-tag-page']) {
    const meta = ctx.loaderData[id]?.meta;
    if (meta) {
      return {
        type: 'collection',
        title: meta.metaTitle || meta.title,
        description: meta.metaDescription || meta.description || '',
      };
    }
  }

  const landingConfig = (ctx.loaderData['ideat-landing-root'] || ctx.loaderData['ideat-landing-page'])?.homepageConfig;
  if (landingConfig) {
    return {
      type: 'collection',
      title: landingConfig.metaTitle || landingConfig.title,
      description: landingConfig.metaDescription || landingConfig.description || '',
    };
  }
  return undefined;
};
